import React, { useCallback,useRef, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import type { ComplianceRuleDocument, ProviderRuleResponse } from '@features/rules/types';
import { Box, Stack } from '@mui/material';
import { useModal } from '@store/hooks/useModal';

import { useJotCoachRules } from '../store/hooks';

import { ComplianceRuleIndicator } from './ComplianceRuleIndicator';
import { ComplianceRulePopover } from './ComplianceRulePopover';

export const ComplianceRuleIndicatorsRow: React.FC = () => {
  const { rules: complianceRules, isLoading } = useJotCoachRules();
  const { open: openModal } = useModal();
  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const indicatorRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const closeTimeoutRef = useRef<NodeJS.Timeout>();

  const cancelCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  }, []);

  const handleOpenModal = useCallback((rule: typeof complianceRules[0]) => {
    // Convert ComplianceRule to ProviderRuleResponse format for the modal
    const complianceRuleDoc: ComplianceRuleDocument = {
      rule_id: rule.id,
      body_md: rule.body_md,
      selector_text: rule.selector_text,
      title: rule.title || null,
      always_select: rule.always_select || false,
      state: rule.state || 'active',
      facets: rule.facets,
      source: rule.source,
    };

    const providerRuleResponse: ProviderRuleResponse = {
      rule: complianceRuleDoc,
      rule_id: rule.id,
      rule_type: 'compliance',
      is_global: false, // JotCoach rules are typically provider rules
      is_disabled: false,
      organization_id: null,
    };

    openModal(MODAL_TYPES.RULE_DETAIL, {
      rule: providerRuleResponse,
      isAdmin: false,
    });
  }, [openModal]);

  const handleIndicatorEnter = useCallback((ruleId: string) => {
    cancelCloseTimeout();
    setHoveredRuleId(ruleId);
  }, [cancelCloseTimeout]);

  const handleIndicatorLeave = useCallback(() => {
    // Delay closing to allow mouse to move to popover
    // This timeout will be cancelled if mouse enters popover
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRuleId(null);
    }, 150);
  }, [cancelCloseTimeout]);

  const handlePopoverMouseEnter = useCallback(() => {
    // Cancel the close timeout when mouse enters popover
    cancelCloseTimeout();
  }, [cancelCloseTimeout]);

  const handlePopoverMouseLeave = useCallback(() => {
    // Start close timeout when mouse leaves popover
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRuleId(null);
    }, 150);
  }, [cancelCloseTimeout]);

  const handlePopoverClose = useCallback(() => {
    cancelCloseTimeout();
    setHoveredRuleId(null);
  }, [cancelCloseTimeout]);

  if (isLoading || complianceRules.length === 0) {
    return null;
  }

  const hoveredRule = hoveredRuleId
    ? complianceRules.find(rule => rule.id === hoveredRuleId) ?? null
    : null;

  const anchorEl = hoveredRuleId ? indicatorRefs.current[hoveredRuleId] : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
        }}
      >
        {complianceRules.map(rule => (
          <Box
            key={rule.id}
            ref={(el: HTMLElement | null) => {
              indicatorRefs.current[rule.id] = el;
            }}
          >
            <ComplianceRuleIndicator
              rule={rule}
              isHovered={hoveredRuleId === rule.id}
              onMouseEnter={() => handleIndicatorEnter(rule.id)}
              onMouseLeave={handleIndicatorLeave}
              onClick={() => handleOpenModal(rule)}
            />
          </Box>
        ))}
      </Stack>

      <ComplianceRulePopover
        rule={hoveredRule}
        anchorEl={anchorEl}
        open={Boolean(hoveredRule && anchorEl)}
        onClose={handlePopoverClose}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      />
    </Box>
  );
};

